import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle,
  Clock,
  CreditCard,
  Wallet,
  Shield,
  ExternalLink,
  Zap,
  DollarSign,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { usePayments } from '../../hooks/payment/usePayments';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

export const PaymentOnboarding: React.FC = () => {
  const theme = useTheme();
  const { settings, settingsLoading, startOnboarding, refreshSettings, syncStripeAccount } =
    usePayments();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const [error, setError] = useState<string | null>(null);
  const [isOnboarding, setIsOnboarding] = useState(false);

  const handleRefresh = async () => {
    await syncStripeAccount();
    refreshSettings();
  };

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const handleStartOnboarding = async () => {
    const orgEmail = selectedOrganization?.client?.email || '';
    if (!orgEmail) {
      setError('Organization email not found. Please contact support.');
      return;
    }

    try {
      setError(null);
      setIsOnboarding(true);
      await startOnboarding(orgEmail);
      // The startOnboarding function now redirects, so we won't reach here
    } catch (err: any) {
      setError(err.message || 'Failed to start onboarding');
      setIsOnboarding(false);
    }
  };

  if (settingsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Complete state
  if (settings?.onboardingStatus === 'COMPLETE' && settings?.chargesEnabled) {
    return (
      <Box>
        {/* Success Banner */}
        <Box
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            border: `2px solid ${theme.palette.success.main}`,
            borderRadius: 2,
            p: 3,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle size={32} color="white" />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Payment Processing Active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your Stripe Connect account is configured and ready to accept payments from customers.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExternalLink size={16} />}
            onClick={handleRefresh}
          >
            Refresh Status
          </Button>
        </Box>

        {/* Account Details Grid */}
        <Grid container spacing={2}>
          {/* Payment Status Card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Shield size={20} color={theme.palette.primary.main} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Account Status
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Onboarding Status
                    </Typography>
                    <Chip
                      label={settings.onboardingStatus}
                      color="success"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Capabilities
                    </Typography>
                    <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                      <Chip
                        label="Charges"
                        color={settings.chargesEnabled ? 'success' : 'default'}
                        size="small"
                        variant={settings.chargesEnabled ? 'filled' : 'outlined'}
                      />
                      <Chip
                        label="Payouts"
                        color={settings.payoutsEnabled ? 'success' : 'default'}
                        size="small"
                        variant={settings.payoutsEnabled ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Account Details Card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CreditCard size={20} color={theme.palette.primary.main} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Account Details
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Stripe Account ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ mt: 0.5 }}>
                      {settings.stripeConnectAccountId}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Billing Email
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {settings.billingEmail || 'Not set'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Settings Card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Wallet size={20} color={theme.palette.primary.main} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Platform Settings
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Platform Fee
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {settings.platformFeePercent}%
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Minimum Payout
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      ${(settings.minPayoutAmountCents / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Box mt={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Next Steps
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
              startIcon={<ExternalLink size={16} />}
            >
              Open Stripe Dashboard
            </Button>
            <Button variant="outlined" size="small" onClick={handleRefresh}>
              Refresh Account Status
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Pending state
  if (settings?.onboardingStatus === 'PENDING') {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 4,
          maxWidth: 500,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <Clock size={40} color={theme.palette.warning.main} />
        </Box>

        <Typography variant="h5" fontWeight={700} gutterBottom>
          Setup In Progress
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          Your Stripe account setup is pending. Please complete the onboarding process to start
          accepting payments.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleStartOnboarding}
            startIcon={<ExternalLink size={18} />}
            sx={{ borderRadius: 2 }}
          >
            Continue Setup
          </Button>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={<RefreshCw size={18} />}
            sx={{ borderRadius: 2 }}
          >
            Refresh Status
          </Button>
        </Box>
      </Box>
    );
  }

  // Not started state
  const features = [
    {
      icon: <CreditCard size={20} />,
      title: 'Accept Card Payments',
      description: 'Accept all major credit and debit cards from customers',
    },
    {
      icon: <DollarSign size={20} />,
      title: 'Automatic Invoicing',
      description: 'Generate and send professional invoices for bookings',
    },
    {
      icon: <Zap size={20} />,
      title: 'Instant Payouts',
      description: 'Get your earnings transferred to your bank account',
    },
    {
      icon: <Lock size={20} />,
      title: 'Secure & Compliant',
      description: 'PCI-compliant payment processing powered by Stripe',
    },
  ];

  return (
    <Box>
      <Grid container spacing={4}>
        {/* Left side - Info and CTA */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <CreditCard size={32} color={theme.palette.primary.main} />
            </Box>

            <Typography variant="h5" fontWeight={700} gutterBottom>
              Start Accepting Payments
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              Connect your Stripe account to accept payments for bookings, send invoices, and manage
              your earnings all in one place.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              disableElevation
              onClick={handleStartOnboarding}
              disabled={isOnboarding}
              startIcon={
                isOnboarding ? <CircularProgress size={18} color="inherit" /> : <Zap size={18} />
              }
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            >
              {isOnboarding ? 'Redirecting to Stripe...' : 'Setup Payment'}
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Shield size={14} color={theme.palette.text.secondary} />
              <Typography variant="caption" color="text.secondary">
                Secure setup powered by Stripe Connect
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right side - Features */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What you'll get
              </Typography>
              <List disablePadding>
                {features.map((feature, index) => (
                  <ListItem
                    key={index}
                    disablePadding
                    sx={{
                      py: 1.5,
                      borderBottom: index < features.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.title}
                      secondary={feature.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
